/**
 * DashboardMap — Google Maps surface with deal pins + inspector region overlays.
 *
 * The map mounts once. Pin and circle layers reconcile in place as the
 * filtered deal/inspector arrays change — never re-create the Map instance
 * (re-mounting eats the user's pan/zoom state and burns map loads).
 *
 * Empty states:
 *   - No deals → "No deals to plot" overlay, map still visible.
 *   - Loading → translucent skeleton overlay so the map shape is preserved.
 *   - No API key → friendly "key missing" panel with the env var name.
 *
 * Render order is deliberate: inspector circles below pins (z-index handled
 * by Google Maps), pins colour-coded by deal verdict.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPinOff } from 'lucide-react';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer } from '@deck.gl/layers';
import type { Feature, Geometry } from 'geojson';
import type { DealWithSuburb } from '../../hooks/useDealsWithSuburbs';
import type { LeadWithSuburb } from '../../hooks/useLeadsWithSuburbs';
import type { Inspector } from '../../types';
import { verdictColour } from '../../utils/regionValidation';
import { getSupabase } from '../../lib/supabase';
import { LEAD_VIOLET, LEAD_TO_BOOK_VIOLET, leadDisplayName } from '../../lib/leadDisplay';
import { fetchGeojsonCached } from '../../lib/geojsonCache';
import { loadAllSuburbZones, normaliseSuburbName, type SuburbZoneVerdict } from '../../lib/suburbs';

const SUBURB_GEOJSON_URL = '/suburbs-qld-nsw.geojson';

// Properties carried on each suburb polygon in the geojson. deck.gl's
// GeoJsonLayer is generic over the PROPERTIES type; the accessor then receives
// a full Feature<Geometry, SuburbProps>.
interface SuburbProps {
  name?: string;
  state?: string;
  [k: string]: unknown;
}
type SuburbFeature = Feature<Geometry, SuburbProps>;

// Hex "#rrggbb" → [r,g,b] for deck.gl colour accessors (want RGBA 0–255, not
// CSS strings). Memoised — the same handful of verdict colours resolve across
// hundreds of polygons.
const _hexRgbCache = new Map<string, [number, number, number]>();
function hexToRgb(hex: string): [number, number, number] {
  const cached = _hexRgbCache.get(hex);
  if (cached) return cached;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  const rgb: [number, number, number] = Number.isNaN(n)
    ? [107, 114, 128]
    : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  _hexRgbCache.set(hex, rgb);
  return rgb;
}

interface Props {
  deals: DealWithSuburb[];
  leads?: LeadWithSuburb[];
  showLeads?: boolean;
  inspectors: Inspector[];
  selectedDealId: string | null;
  selectedLeadId?: string | null;
  onSelectDeal: (dealId: string | null) => void;
  onSelectLead?: (leadId: string | null) => void;
  loading?: boolean;
}

// Lead marker palette (violet family) lives in ../../lib/leadDisplay.

// Default centre = Brisbane CBD (covers most QLD/NSW use). Will be overridden
// when the deal set has any pins (fitBounds runs on first non-empty render).
const DEFAULT_CENTER = { lat: -27.4698, lng: 153.0251 };
const DEFAULT_ZOOM = 7;
const SERVICE_RADIUS_METERS = 75_000;
const INSPECTOR_RING_COLOR = '#7c3aed';

function getReadyPromise(): Promise<void> {
  const w = window as Window & { __v9MapsReady?: Promise<void> };
  return w.__v9MapsReady ?? Promise.resolve();
}

interface ZoneCentroid {
  zone: string;
  lat: number;
  lng: number;
}

let _centroidCache: { ts: number; data: ZoneCentroid[] } | null = null;
const CENTROID_TTL_MS = 5 * 60_000;

async function loadZoneCentroids(): Promise<ZoneCentroid[]> {
  if (_centroidCache && Date.now() - _centroidCache.ts < CENTROID_TTL_MS) {
    return _centroidCache.data;
  }
  const supabase = getSupabase();
  const { data } = await supabase
    .from('v9_suburb_cache')
    .select('zone, raw_row')
    .not('zone', 'is', null);
  if (!data) return [];
  const acc = new Map<string, { lat: number; lng: number; n: number }>();
  for (const row of data as { zone: string; raw_row: Record<string, unknown> | null }[]) {
    const lat = pickNum(row.raw_row, ['lat', 'latitude', 'Lat', 'Latitude', 'centroid_lat']);
    const lng = pickNum(row.raw_row, ['lng', 'longitude', 'Lng', 'Longitude', 'Long', 'centroid_lng']);
    if (lat == null || lng == null) continue;
    const cur = acc.get(row.zone) ?? { lat: 0, lng: 0, n: 0 };
    cur.lat += lat;
    cur.lng += lng;
    cur.n += 1;
    acc.set(row.zone, cur);
  }
  const list: ZoneCentroid[] = [];
  for (const [zone, v] of acc.entries()) {
    if (v.n === 0) continue;
    list.push({ zone, lat: v.lat / v.n, lng: v.lng / v.n });
  }
  _centroidCache = { ts: Date.now(), data: list };
  return list;
}

function pickNum(obj: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

// Resolve an entity with no upstream coordinates to a map position: the
// centroid of its resolved zone + a small deterministic jitter so several
// entities in one zone don't stack into a single dot. The jitter is hashed
// off a stable id (lead or deal id), giving the same offset every render.
// Returns null when the zone has no known centroid — those can't be placed.
// Shared by both lead and deal coordinate fallbacks; do not duplicate.
function centroidWithJitter(
  zone: string | null | undefined,
  id: string,
  centroidByZone: Map<string, ZoneCentroid>,
): { lat: number; lng: number } | null {
  if (!zone) return null;
  const c = centroidByZone.get(zone);
  if (!c) return null;
  // Hash the id to a stable offset within ~±0.08° (~9km) of the centroid.
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const jLat = (((h & 0xff) / 255) - 0.5) * 0.16;
  const jLng = ((((h >> 8) & 0xff) / 255) - 0.5) * 0.16;
  return { lat: c.lat + jLat, lng: c.lng + jLng };
}

// Marker icons are referentially-stable Symbol objects, keyed by colour.
// Without this cache, every reconcile pass allocates fresh icon objects for
// every marker — at 500+ pins that's a measurable GC + draw cost during
// pan/zoom. Cache size is bounded by the small palette of verdict colours.
const _markerIconCache = new Map<string, google.maps.SymbolIcon>();
function getMarkerIcon(colour: string): google.maps.SymbolIcon {
  const cached = _markerIconCache.get(colour);
  if (cached) return cached;
  const icon: google.maps.SymbolIcon = {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 7,
    fillColor: colour,
    fillOpacity: 0.9,
    strokeColor: '#0a0a0a',
    strokeWeight: 2,
  };
  _markerIconCache.set(colour, icon);
  return icon;
}

// Lead markers use a diamond (rotated square) so they're distinguishable from
// deal circles at a glance — different geometry, not just a different colour.
// Lower fillOpacity on the remarketing pool keeps "lead_to_book" reading as the
// stronger, book-worthy signal. Cached per colour like the deal icons.
const DIAMOND_PATH = 'M 0,-1 L 1,0 L 0,1 L -1,0 Z';
const _leadIconCache = new Map<string, google.maps.SymbolIcon>();
function getLeadIcon(colour: string, opacity: number): google.maps.SymbolIcon {
  const cacheKey = `${colour}:${opacity}`;
  const cached = _leadIconCache.get(cacheKey);
  if (cached) return cached;
  const icon: google.maps.SymbolIcon = {
    path: DIAMOND_PATH,
    scale: 6,
    fillColor: colour,
    fillOpacity: opacity,
    strokeColor: '#0a0a0a',
    strokeWeight: 1.5,
  };
  _leadIconCache.set(cacheKey, icon);
  return icon;
}

// Above this marker count, attach a bounds_changed listener and toggle
// off-viewport pins to `setVisible(false)` so the Maps SDK skips their draw
// path. IntersectionObserver doesn't work — markers are canvas/WebGL, not DOM.
const VIEWPORT_CULL_THRESHOLD = 100;

// Dark map style — keeps the map subordinate to the brand UI.
const MAP_STYLE: unknown[] = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#020617' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
];

/**
 * Build the tooltip body using DOM nodes (not innerHTML) so deal text never
 * gets HTML-interpreted. CRM names occasionally contain `<` / `>` and we'd
 * rather render them literally than render unintended markup.
 */
function buildTooltipContent(name: string, address: string | null, suburb: string | null, zone: string | null): HTMLElement {
  const root = document.createElement('div');
  root.style.fontFamily = 'Inter, system-ui, sans-serif';
  root.style.minWidth = '180px';

  const title = document.createElement('div');
  title.textContent = name;
  title.style.fontWeight = '600';
  title.style.color = '#0f172a';
  title.style.fontSize = '12px';
  root.appendChild(title);

  if (address) {
    const addr = document.createElement('div');
    addr.textContent = address;
    addr.style.color = '#475569';
    addr.style.fontSize = '11px';
    addr.style.marginTop = '2px';
    root.appendChild(addr);
  }

  if (suburb || zone) {
    const meta = document.createElement('div');
    meta.textContent = [suburb, zone].filter(Boolean).join(' · ');
    meta.style.color = '#64748b';
    meta.style.fontSize = '11px';
    meta.style.marginTop = '2px';
    root.appendChild(meta);
  }
  return root;
}

export function DashboardMap({
  deals,
  leads = [],
  showLeads = true,
  inspectors,
  selectedDealId,
  selectedLeadId = null,
  onSelectDeal,
  onSelectLead,
  loading,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const leadMarkersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const circlesRef = useRef<google.maps.Circle[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const cullListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const [ready, setReady] = useState(false);
  const [keyMissing, setKeyMissing] = useState(false);
  const [centroids, setCentroids] = useState<ZoneCentroid[]>([]);
  const fittedRef = useRef(false);

  // deck.gl suburb-zone overlay — GPU-rendered polygons coloured by verdict,
  // replacing the fuzzy service-radius circles as the zone visualisation. The
  // overlay is created once and bound to the map; the layer rebuilds when the
  // polygons or the suburb→verdict join changes.
  const deckOverlayRef = useRef<GoogleMapsOverlay | null>(null);
  const [suburbFeatures, setSuburbFeatures] = useState<SuburbFeature[]>([]);
  const [suburbZones, setSuburbZones] = useState<Map<string, SuburbZoneVerdict>>(new Map());
  // Hover tooltip for a suburb polygon — container-relative px + the joined data.
  const [zoneHover, setZoneHover] = useState<
    { name: string; zone: string | null; verdict: string | null; x: number; y: number } | null
  >(null);

  // Marker click listeners are bound once at marker creation, but onSelectDeal
  // changes identity on every parent render. Route the call through a ref so
  // existing markers always invoke the latest callback without rebinding.
  const onSelectDealRef = useRef(onSelectDeal);
  const onSelectLeadRef = useRef(onSelectLead);
  useEffect(() => {
    onSelectDealRef.current = onSelectDeal;
    onSelectLeadRef.current = onSelectLead;
  });

  // Initialise the Map exactly once.
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setKeyMissing(true);
      return;
    }
    let cancelled = false;
    void getReadyPromise().then(() => {
      if (cancelled || !containerRef.current || !window.google?.maps) return;
      mapRef.current = new window.google.maps.Map(containerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
        backgroundColor: '#0a0a0a',
        styles: MAP_STYLE as google.maps.MapTypeStyle[],
      });
      infoWindowRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
      setReady(true);
    });

    void loadZoneCentroids().then((list) => {
      if (!cancelled) setCentroids(list);
    });

    return () => {
      cancelled = true;
      cullListenerRef.current?.remove();
      cullListenerRef.current = null;
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current.clear();
      leadMarkersRef.current.forEach((m) => m.setMap(null));
      leadMarkersRef.current.clear();
      circlesRef.current.forEach((c) => c.setMap(null));
      circlesRef.current = [];
      infoWindowRef.current?.close();
    };
  }, []);

  // Load the suburb polygons (from the persistent Cache API — populated once,
  // no re-download) + the tenant's suburb→zone/verdict join, once on mount.
  // Parse + filter run on the main thread; deck.gl then renders on the GPU.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [res, zones] = await Promise.all([
          fetchGeojsonCached(SUBURB_GEOJSON_URL),
          loadAllSuburbZones(),
        ]);
        if (cancelled || !res.ok) return;
        const gj = (await res.json()) as { features?: SuburbFeature[] };
        if (cancelled || !Array.isArray(gj.features)) return;
        // Only keep polygons for suburbs this tenant actually has a zone for —
        // both correct (show only their zones) and a big perf cut (hundreds of
        // polygons, not all 7,779).
        const features = gj.features.filter((f) =>
          zones.has(normaliseSuburbName(f.properties?.name)),
        );
        setSuburbZones(zones);
        setSuburbFeatures(features);
      } catch (err) {
        console.warn('[DashboardMap] suburb zone overlay load failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // deck.gl overlay LIFECYCLE — create once the map is ready, finalize on
  // unmount. StrictMode-safe: create in body, finalize in cleanup.
  useEffect(() => {
    if (!ready || !mapRef.current) return undefined;
    const overlay = new GoogleMapsOverlay({ layers: [] });
    overlay.setMap(mapRef.current);
    deckOverlayRef.current = overlay;
    return () => {
      try {
        overlay.finalize();
      } catch {
        /* already gone */
      }
      if (deckOverlayRef.current === overlay) deckOverlayRef.current = null;
    };
  }, [ready]);

  // deck.gl LAYER — suburb polygons coloured by verdict. Rebuilt when the
  // polygon set or the zone/verdict join changes. Hover shows suburb + zone +
  // verdict; click selects nothing yet (zones aren't selectable entities).
  useEffect(() => {
    const overlay = deckOverlayRef.current;
    if (!overlay) return;
    if (suburbFeatures.length === 0) {
      overlay.setProps({ layers: [] });
      return;
    }

    const fillFor = (f: SuburbFeature): [number, number, number, number] => {
      const z = suburbZones.get(normaliseSuburbName(f.properties?.name));
      const [r, g, b] = hexToRgb(verdictColour(z?.verdictLabel ?? null));
      return [r, g, b, 70];
    };
    const lineFor = (f: SuburbFeature): [number, number, number, number] => {
      const z = suburbZones.get(normaliseSuburbName(f.properties?.name));
      const [r, g, b] = hexToRgb(verdictColour(z?.verdictLabel ?? null));
      return [r, g, b, 180];
    };

    // GeoJsonLayer is generic over the properties type; the accessor then
    // receives a Feature<Geometry, SuburbProps>.
    const layer = new GeoJsonLayer<SuburbProps>({
      id: 'suburb-zones',
      data: suburbFeatures,
      stroked: true,
      filled: true,
      lineWidthUnits: 'pixels',
      getLineWidth: 0.8,
      lineWidthMinPixels: 0.5,
      getFillColor: fillFor,
      getLineColor: lineFor,
      pickable: true,
      autoHighlight: true,
      highlightColor: [0, 0, 0, 40], // subtle darken, keeps the verdict colour readable
      updateTriggers: {
        getFillColor: [suburbZones],
        getLineColor: [suburbZones],
      },
      onHover: (info) => {
        if (info.object) {
          const name = info.object.properties?.name ?? '';
          const z = suburbZones.get(normaliseSuburbName(name));
          setZoneHover({ name, zone: z?.zone ?? null, verdict: z?.verdictLabel ?? null, x: info.x, y: info.y });
        } else {
          setZoneHover(null);
        }
      },
    });

    overlay.setProps({ layers: [layer] });
  }, [suburbFeatures, suburbZones]);

  // Inspector region overlays. Recreate cheaply — at most ~20 inspectors.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    circlesRef.current.forEach((c) => c.setMap(null));
    circlesRef.current = [];
    if (!window.google?.maps) return;

    const centroidByZone = new Map(centroids.map((c) => [c.zone, c]));
    for (const ins of inspectors) {
      if (!ins.region_code) continue;
      const c = centroidByZone.get(ins.region_code);
      if (!c) continue;
      const circle = new window.google.maps.Circle({
        center: { lat: c.lat, lng: c.lng },
        radius: SERVICE_RADIUS_METERS,
        map: mapRef.current,
        fillColor: INSPECTOR_RING_COLOR,
        fillOpacity: 0.04,
        strokeColor: INSPECTOR_RING_COLOR,
        strokeOpacity: 0.4,
        strokeWeight: 1,
        clickable: false,
      });
      circlesRef.current.push(circle);
    }
  }, [ready, inspectors, centroids]);

  // Zone centroid lookup — shared by deal + lead placement. Entities with no
  // upstream coordinates pin at the centroid of their resolved zone (+ jitter).
  const centroidByZone = useMemo(
    () => new Map(centroids.map((c) => [c.zone, c])),
    [centroids],
  );

  // Deal markers. Reconcile by id so pan/zoom on selection stays smooth.
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const seen = new Set<string>();
    const bounds = new window.google.maps.LatLngBounds();
    let anyPin = false;

    for (const item of deals) {
      const { deal, lat, lng, regionMatch } = item;
      // Real coords win; otherwise fall back to the resolved zone centroid +
      // deterministic jitter (same placement leads use). Deals with neither
      // coords nor a resolvable zone stay off the map (sidebar list only).
      const pos =
        lat != null && lng != null
          ? { lat, lng }
          : centroidWithJitter(regionMatch?.zone, deal.id, centroidByZone);
      if (!pos) continue;
      seen.add(deal.id);
      anyPin = true;
      const colour = verdictColour(regionMatch?.verdict?.label ?? null);

      let marker = markersRef.current.get(deal.id);
      if (!marker) {
        marker = new window.google.maps.Marker({
          map,
          position: pos,
          title: deal.name,
          icon: getMarkerIcon(colour),
        });
        const dealId = deal.id;
        marker.addListener('click', () => {
          onSelectDealRef.current(dealId);
        });
        marker.addListener('mouseover', () => {
          if (!infoWindowRef.current) return;
          infoWindowRef.current.setContent(
            buildTooltipContent(
              deal.name,
              deal.address,
              regionMatch?.suburb ?? null,
              regionMatch?.zone ?? null,
            ),
          );
          infoWindowRef.current.open(map, marker);
        });
        marker.addListener('mouseout', () => {
          infoWindowRef.current?.close();
        });
        markersRef.current.set(deal.id, marker);
      } else {
        marker.setPosition(pos);
      }

      bounds.extend(pos);
    }

    // Remove markers that are no longer in the deal set.
    for (const [id, marker] of markersRef.current.entries()) {
      if (!seen.has(id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
      }
    }

    // Fit bounds once on first non-empty render so we don't yank the user's
    // view every time they filter. Re-fit only after explicit empty → repopulated.
    if (anyPin && !fittedRef.current) {
      if (!bounds.isEmpty()) map.fitBounds(bounds, 64);
      fittedRef.current = true;
    } else if (!anyPin) {
      fittedRef.current = false;
    }

    // Viewport culling — only attach above the threshold so light loads pay
    // zero overhead. Hides off-screen markers so the SDK skips their draw
    // path during pan/zoom on dense scenes.
    if (cullListenerRef.current) {
      cullListenerRef.current.remove();
      cullListenerRef.current = null;
    }
    if (markersRef.current.size > VIEWPORT_CULL_THRESHOLD) {
      const cull = () => {
        const b = map.getBounds();
        if (!b) return;
        for (const m of markersRef.current.values()) {
          const p = m.getPosition();
          m.setVisible(p ? b.contains(p) : true);
        }
      };
      cull();
      cullListenerRef.current = map.addListener('bounds_changed', cull);
    } else {
      // Ensure all markers are visible when we drop below the threshold.
      for (const m of markersRef.current.values()) m.setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, deals, centroidByZone]);

  // Resolve a lead to a map position via the shared centroid+jitter fallback.
  // Returns null when the lead has no resolved zone — those can't be placed,
  // they live in the sidebar list only.
  const leadPosition = (item: LeadWithSuburb): { lat: number; lng: number } | null =>
    centroidWithJitter(item.regionMatch?.zone, item.lead.id, centroidByZone);

  // Lead markers. Reconciled independently of deals, keyed by lead id, distinct
  // diamond geometry + violet palette. No marker churn on rerender.
  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;
    const map = mapRef.current;
    const seen = new Set<string>();

    if (showLeads) {
      for (const item of leads) {
        const pos = leadPosition(item);
        if (!pos) continue;
        seen.add(item.lead.id);
        const colour = item.leadStatus === 'lead_to_book' ? LEAD_TO_BOOK_VIOLET : LEAD_VIOLET;
        const opacity = item.leadStatus === 'lead_to_book' ? 0.92 : 0.6;

        let marker = leadMarkersRef.current.get(item.lead.id);
        if (!marker) {
          marker = new window.google.maps.Marker({
            map,
            position: pos,
            title: leadDisplayName(item.lead, item.lead.id),
            icon: getLeadIcon(colour, opacity),
            zIndex: item.leadStatus === 'lead_to_book' ? 20 : 10,
          });
          const leadId = item.lead.id;
          marker.addListener('click', () => onSelectLeadRef.current?.(leadId));
          marker.addListener('mouseover', () => {
            if (!infoWindowRef.current) return;
            infoWindowRef.current.setContent(
              buildTooltipContent(
                leadDisplayName(item.lead, item.lead.id),
                item.lead.address,
                item.regionMatch?.suburb ?? null,
                item.leadStatus === 'lead_to_book' ? 'Lead to book' : 'Lead',
              ),
            );
            infoWindowRef.current.open(map, marker);
          });
          marker.addListener('mouseout', () => infoWindowRef.current?.close());
          leadMarkersRef.current.set(item.lead.id, marker);
        } else {
          marker.setPosition(pos);
          marker.setIcon(getLeadIcon(colour, opacity));
        }
      }
    }

    // Drop lead markers no longer present (or hidden when showLeads is off).
    for (const [id, marker] of leadMarkersRef.current.entries()) {
      if (!seen.has(id)) {
        marker.setMap(null);
        leadMarkersRef.current.delete(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, leads, showLeads, centroidByZone]);

  // Pan to the selected pin.
  useEffect(() => {
    if (!ready || !mapRef.current || !selectedDealId) return;
    const item = deals.find((d) => d.deal.id === selectedDealId);
    if (item?.lat != null && item.lng != null) {
      mapRef.current.panTo({ lat: item.lat, lng: item.lng });
    }
  }, [ready, selectedDealId, deals]);

  // Pan to the selected lead.
  useEffect(() => {
    if (!ready || !mapRef.current || !selectedLeadId) return;
    const item = leads.find((l) => l.lead.id === selectedLeadId);
    if (!item) return;
    const pos = leadPosition(item);
    if (pos) mapRef.current.panTo(pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, selectedLeadId, leads, centroidByZone]);

  // Count anything that can be placed: real coords, or a zone with a known
  // centroid (the fallback path). Drives the "No deals to plot" empty state.
  const pinCount = useMemo(
    () =>
      deals.filter(
        (d) =>
          (d.lat != null && d.lng != null) ||
          (d.regionMatch?.zone != null && centroidByZone.has(d.regionMatch.zone)),
      ).length,
    [deals, centroidByZone],
  );

  if (keyMissing) {
    return (
      <div className="relative flex h-full min-h-[300px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02] text-center">
        <MapPinOff className="h-8 w-8 text-neutral-600" />
        <div className="mt-3 text-sm font-medium text-neutral-300">Google Maps key missing</div>
        <p className="mt-1 max-w-xs px-4 text-xs text-neutral-500">
          Set <code className="rounded bg-white/10 px-1 py-0.5">VITE_GOOGLE_MAPS_API_KEY</code> in your Netlify env vars to enable the map.
        </p>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[300px] overflow-hidden rounded-2xl border border-white/10">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Suburb-zone hover tooltip (deck.gl polygons). Positioned at the
          pointer within the map container; pointer-events off so it never
          eats the hover it's describing. */}
      {zoneHover && (
        <div
          className="pointer-events-none absolute z-20 rounded-lg border border-white/10 bg-neutral-900/90 px-2.5 py-1.5 text-xs text-neutral-200 shadow-lg backdrop-blur"
          style={{ left: zoneHover.x + 12, top: zoneHover.y + 12, maxWidth: 220 }}
        >
          <div className="font-semibold text-neutral-100">{zoneHover.name}</div>
          <div className="mt-0.5 text-[11px] text-neutral-400">
            {[zoneHover.zone, zoneHover.verdict].filter(Boolean).join(' · ') || 'No verdict'}
          </div>
        </div>
      )}

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-950/40 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-xs">Loading map…</span>
          </div>
        </div>
      )}

      {ready && loading && pinCount === 0 && (
        <div className="absolute inset-x-4 bottom-4 rounded-xl border border-white/10 bg-neutral-900/85 px-3 py-2 text-xs text-neutral-400 backdrop-blur">
          <Loader2 className="mr-2 inline h-3 w-3 animate-spin" /> Loading deals…
        </div>
      )}

      {ready && !loading && pinCount === 0 && (
        <div className="pointer-events-none absolute inset-x-4 top-4 rounded-xl border border-white/10 bg-neutral-900/85 px-3 py-2 text-xs text-neutral-400 backdrop-blur">
          No deals to plot. Once your CRM deals have addresses, pins will appear here.
        </div>
      )}
    </div>
  );
}
